import { useState } from 'react';
import { useLocale, useTranslate } from '../../application/i18n/LocaleProvider';
import { SHARE_RESULTS, shareApp } from '../../application/pwa/shareApp';
import { ShareIcon } from '../icons/ControlIcons';

/**
 * Share the app itself — the garage, not a car.
 *
 * The whole path for this existed and nothing called it: `shareApp`, the
 * canonical-origin resolver behind it, the card renderer and the two rendered
 * cards were all shipping, and the only way to send somebody Legends was to copy
 * the address out of the URL bar. Orphan code by the repo's own rule, and an
 * odd gap in a product whose brief leads with sharing.
 *
 * In the HEADER rather than at the foot of the garage, where `AppOffers` lives.
 * That row is the one place the app may ASK the reader for something — install
 * this, allow that — and it is deliberately at most one card. Sharing is not a
 * request the app makes; it is a thing the reader chooses to do, and burying it
 * under two dozen cars would make it a thing they never find. Beside the filter
 * control it is two glyphs on a bar the reader already reads.
 *
 * A single button rather than a chooser, unlike a car's. The app has one card in
 * one shape and one address, so there is nothing to choose between: `shareImage`
 * falls down its own ladder — the picture, then the link, then the clipboard —
 * and the reader picks the destination in the OS sheet.
 */
export default function AppShareButton() {
    const { localeCode, languageCode } = useLocale();
    const t = useTranslate();
    const [result, setResult] = useState(null);

    const resultKey = {
        [SHARE_RESULTS.copied]: 'app.share.copied',
        [SHARE_RESULTS.failed]: 'app.share.failed',
    }[result] ?? null;

    return (
        <>
            <button
                type="button"
                className="brand-header__share"
                onClick={async () => setResult(await shareApp({
                    localeCode,
                    languageCode,
                    title: t('app.share.title'),
                    text: t('app.share.invitation'),
                }))}
                aria-label={t('app.share.action')}
            >
                <ShareIcon size={22} />
            </button>

            {/* Always in the document, empty until there is something to say: a
                live region inserted at the moment it gains text is one several
                screen readers never announce. `shared` says nothing — the OS
                sheet the reader just used is its own feedback, and a toast after
                it would be the app talking over the confirmation. */}
            <p className="brand-header__share-result" role="status">
                {resultKey ? t(resultKey) : ''}
            </p>
        </>
    );
}
